import React, { useEffect, useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useTargetAchievementSupportContext } from 'contexts/TargetAchievementSupportContext';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useModal } from 'hooks/useModal';
import { Mode, validateProps } from './TargetUpdateStep1';
import { TargetAccountItem, Target } from '../utils/type';
import { useTargetSupportRouting } from '../hooks/useTargetSupportRouting';
import { TargetPremium } from '../components/features/TargetPremium';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetBasicInfoForm } from '../components/features/share/TargetBasicInfoForm';
import { getTotalAccountsAmount } from '../components/features/update/confirm/TargetUpdateConfirmNew';
import { ChangeYieldCalculationForm } from '../components/features/update/ChangeYieldCalculationForm';
import { ExitSetHowToSaveModal } from '../components/features/update/modal/ExitSetHowToSaveModal';
import { Page, StepArea } from '../components/features/update/StepArea';
import { useBirthday } from '../utils/birthday';

const toAbsMonth = (date: Date): number =>
  date.getFullYear() * 12 + date.getMonth();

export const getRangeMax = (target: Target): number =>
  Math.min(
    500_000,
    Math.ceil(
      target.targetAmount /
        (toAbsMonth(new Date(target.endDate)) - toAbsMonth(new Date()))
    )
  );

const TargetUpdateStep2Internal = ({
  id,
  mode,
  target,
  updateTarget,
  isConfirm,
}: {
  id: string;
  mode: Mode;
  target: Target;
  updateTarget: (data: Target | null) => void;
  isConfirm: boolean;
}) => {
  const {
    goTargetReplaceUpdateStep1,
    goTargetPushUpdateConfirm,
    goTargetReplaceUpdateConfirm,
  } = useTargetSupportRouting();

  const { setIsShowMenu } = useGlobalMenuContext();
  useEffect(() => {
    return setIsShowMenu(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rangeMax = getRangeMax(target);

  const initialReserveAmount =
    mode === 'new' && !target.reserveAmount
      ? Math.round(rangeMax / 2)
      : target.reserveAmount;
  const [reserveAmount, setReserveAmount] = useState<number | null>(
    initialReserveAmount
  );

  const [targetYield, setTargetYield] = useState<number | null>(null);

  const [validateReserveAmount, setValidateReserveAmount] = useState<
    validateProps
  >({ isValid: false });

  const [
    isShowExitModal,
    { hideModal: hideExitModal, showModal: showExitModal },
  ] = useModal();

  const { setBack } = useCustomizeHeaderContext();
  useEffect(() => {
    if (!isConfirm) {
      setBack({ backFunction: showExitModal });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalAccountsAmount = getTotalAccountsAmount(target);
  const selectedAccounts: TargetAccountItem[] =
    target.targetAmount > totalAccountsAmount ? target.accountList : [];

  const excludeAmount =
    target.targetAmount > totalAccountsAmount ? target.excludeAmount : 0;

  const goNext = (): void => {
    const next = (): void => {
      isConfirm
        ? goTargetReplaceUpdateConfirm(id, mode)
        : goTargetPushUpdateConfirm(id, mode);
    };

    const updateTarget_ = {
      ...target,
      reserveAmount: reserveAmount ?? 0,
      yield: targetYield ?? 0,
    };
    updateTarget(updateTarget_);

    validateReserveAmount.isValid && next();
  };

  const goBack = () =>
    isConfirm ? goTargetReplaceUpdateConfirm(id, mode) : showExitModal();

  const onQuit = () => {
    const updateTarget_ = {
      ...target,
      reserveAmount: 0,
      yield: 0,
    };
    updateTarget(updateTarget_);
    goTargetReplaceUpdateStep1(id, mode);
  };

  return (
    <>
      <div className="mt-6">
        {mode === 'new' && <StepArea {...{ currentPage: 'step2' as Page }} />}
        <TargetBasicInfoForm {...{ target: target }} />
        <ChangeYieldCalculationForm
          id={id}
          mode={mode}
          target={target}
          rangeMax={rangeMax}
          selectedAccounts={selectedAccounts}
          reserveAmount={reserveAmount}
          setReserveAmount={setReserveAmount}
          excludeAmount={excludeAmount}
          setTargetYield={setTargetYield}
          validateReserveAmount={validateReserveAmount}
          setValidateReserveAmount={setValidateReserveAmount}
        />
        <div className="flex flex-col mt-6 mb-16 mx-2">
          <button
            className={`flex justify-center items-center mb-4 py-2 text-base font-bold text-white bg-tradewell rounded-lg  ${
              validateReserveAmount.isValid
                ? ''
                : 'opacity-20 cursor-not-allowed'
            }`}
            onClick={goNext}
            disabled={!validateReserveAmount.isValid}>
            {isConfirm ? '変更する' : '次へ'}
          </button>
          <button
            className="flex justify-center items-center py-2 text-base font-bold text-bluegray-700 bg-gray-50 border rounded-lg border-bluegray-400 shadow"
            onClick={goBack}>
            戻る
          </button>
        </div>
      </div>

      <ExitSetHowToSaveModal
        {...{
          hide: hideExitModal,
          isShow: isShowExitModal,
          onQuit,
          onContinueInput: hideExitModal,
        }}
      />
    </>
  );
};

const TargetUpdateStep2 = (): React.ReactElement => {
  useCustomHeaderTitle('つみたて額の設定');
  const { id, mode } = useParams<{
    id: string;
    mode: Mode;
  }>();

  const {
    globalTarget,
    updateGlobalTarget,
    confirmGlobalTarget,
    updateConfirmGlobalTarget,
  } = useTargetAchievementSupportContext();

  const { birthday } = useBirthday();

  const isConfirm = confirmGlobalTarget !== null ? true : false;

  if (!globalTarget || id !== globalTarget.id) {
    return <Redirect to={`/target/${id}`} />;
  }

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <TargetUpdateStep2Internal
        {...{
          id,
          mode,
          target:
            isConfirm && confirmGlobalTarget !== null
              ? confirmGlobalTarget
              : globalTarget,
          updateTarget: isConfirm
            ? updateConfirmGlobalTarget
            : updateGlobalTarget,
          isConfirm,
        }}
      />
    </PremiumCheck>
  );
};

export default TargetUpdateStep2;
